import type { ApiResponse } from "@/types/common";
import type {
  createSubscriptionPayload,
  GetSubscriptionParams,
  GetSubscriptionResponse,
  SubscribedUser,
  SubscriptionPlan,
} from "@/types/subscription";
import { privateRequest } from "../axios";

const ENDPOINTS = {
  SUBSCRIPTION: "/subscription-plan",
  DELETE_SUBSCRIPTION: (id: string | number) => `/subscription-plan/${id}`,
  SUBSCRIBED_USERS: "/subscription",
  USER_SUBSCRIPTION: "/subscription/me",
  SUBSCRIPTION_HISTORY: "/subscription/history",
  QUEUED_SUBSCRIPTION: "/subscription/queued",
  FAQS: "/faqs",
};

const getAllSubscribedUsers = async (
  params?: GetSubscriptionParams,
): Promise<GetSubscriptionResponse<SubscribedUser>> => {
  const response = await privateRequest.get<
    ApiResponse<GetSubscriptionResponse<SubscribedUser>>
  >(ENDPOINTS.SUBSCRIBED_USERS, { params });
  return {
    results: response?.data?.data?.results ?? [],
    totalRecord: response?.data?.data?.totalRecord ?? 0,
  };
};

const getAllSubscriptions = async (
  params?: GetSubscriptionParams,
): Promise<GetSubscriptionResponse<SubscriptionPlan>> => {
  const response = await privateRequest.get<
    ApiResponse<GetSubscriptionResponse<SubscriptionPlan>>
  >(ENDPOINTS.SUBSCRIPTION, { params });
  return {
    results: response?.data?.data?.results ?? [],
    totalRecord: response?.data?.data?.totalRecord ?? 0,
  };
};

const createSubscription = async (
  payload?: createSubscriptionPayload,
): Promise<ApiResponse<any>> => {
  const response = await privateRequest.post<ApiResponse<any>>(
    ENDPOINTS.SUBSCRIPTION,
    payload,
  );
  return response?.data;
};

const deleteSubscription = async (
  id: string | number,
): Promise<ApiResponse<any>> => {
  const response = await privateRequest.delete<ApiResponse<any>>(
    ENDPOINTS.DELETE_SUBSCRIPTION(id),
  );
  return response?.data;
};

const exportSubscriptions = async () => {
  return privateRequest.get(ENDPOINTS.SUBSCRIPTION, {
    params: {
      isExport: true,
    },
    responseType: "blob",
  })
}

const getUserSubscription = async (): Promise<ApiResponse<SubscribedUser>> => {
  const response = await privateRequest.get<ApiResponse<SubscribedUser>>(
    ENDPOINTS.USER_SUBSCRIPTION,
  );
  return response.data;
};

const getOptionalUserSubscription = async (): Promise<SubscribedUser | null> => {
  const response = await privateRequest.get<ApiResponse<SubscribedUser>>(
    ENDPOINTS.USER_SUBSCRIPTION,
    {
      validateStatus: (status) =>
        (status >= 200 && status < 300) || status === 404,
    },
  );

  if (response.status === 404) {
    return null;
  }

  return response.data?.data ?? null;
};

const getSubscriptionHistory = async (
  params?: GetSubscriptionParams,
): Promise<ApiResponse<GetSubscriptionResponse<SubscribedUser>>> => {
  const response = await privateRequest.get<
    ApiResponse<GetSubscriptionResponse<SubscribedUser>>
  >(ENDPOINTS.SUBSCRIPTION_HISTORY, { params });
  return response.data;
};

const cancelQueuedSubscription = async (): Promise<ApiResponse<unknown>> => {
  const response = await privateRequest.delete<ApiResponse<unknown>>(
    ENDPOINTS.QUEUED_SUBSCRIPTION,
  );
  return response.data;
};

const getFaqs = async (): Promise<ApiResponse<any>> => {
  const response = await privateRequest.get<ApiResponse<any>>(ENDPOINTS.FAQS);
  return response.data;
};

const SubscriptionService = {
  getAllSubscriptions,
  createSubscription,
  deleteSubscription,
  getAllSubscribedUsers,
  exportSubscriptions,
  getUserSubscription,
  getOptionalUserSubscription,
  getSubscriptionHistory,
  cancelQueuedSubscription,
  getFaqs,
};

export default SubscriptionService;
